import { useEffect, useMemo, useState } from 'react';
import { Alert, BackgroundImage, Box, Button, Center, Paper, Stack, Text, Title } from '@mantine/core';
import { useAppColorScheme } from '../lib/colorScheme';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

/** Erlaubte Pfad-Prefixe für den `next`-Parameter nach Auth-Callback */
const ALLOWED_NEXT_PREFIXES = [
  '/reset-password',
  '/calendar',
  '/news',
  '/choir',
  '/playbacks',
  '/config',
  '/members',
  '/',
];

/**
 * Schützt vor Open-Redirect-Angriffen:
 * - Muss mit '/' starten (keine externen URLs)
 * - Darf nicht mit '//' starten (Protocol-relative URLs)
 * - Muss zu einem erlaubten Pfad-Prefix gehören (Whitelist)
 */
function safeNextPath(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null;
  // Pfad ohne Query-Parameter und Hash für den Prefix-Check
  const pathOnly = raw.split('?')[0].split('#')[0];
  const isAllowed = ALLOWED_NEXT_PREFIXES.some(
    (prefix) => pathOnly === prefix || pathOnly.startsWith(prefix === '/' ? '/' : prefix + '/'),
  );
  if (!isAllowed) return null;
  return raw;
}

function readTypeFromUrl(url: URL): string | null {
  const qp = url.searchParams.get('type');
  if (qp) return qp;
  const hash = url.hash?.startsWith('#') ? url.hash.slice(1) : url.hash;
  const hp = new URLSearchParams(hash);
  return hp.get('type');
}

function attachResetType(path: string, type: string | null): string {
  if (!type || (type !== 'recovery' && type !== 'invite')) return path;
  if (!path.startsWith('/reset-password')) return path;
  if (/[?&]type=/.test(path)) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}type=${encodeURIComponent(type)}`;
}

export function AuthCallbackPage() {
  const nav = useNavigate();
  const { colorScheme } = useAppColorScheme();

  const [ready, setReady] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);

  const targetPath = useMemo(() => {
    const url = new URL(window.location.href);
    const type = readTypeFromUrl(url);
    const next = safeNextPath(url.searchParams.get('next'));
    if (next) return attachResetType(next, type);

    if (type === 'recovery' || type === 'invite') return `/reset-password?type=${type}`;

    return '/';
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const url = new URL(window.location.href);

        // PKCE flow: Supabase redirects with ?code=...
        const code = url.searchParams.get('code');
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            // Tolerate the case where another handler already exchanged the code.
            const { data } = await supabase.auth.getSession();
            if (!data.session) throw error;
          }
            // Nur wenn der Parameter noch existiert, ersetzen und return
            if (url.searchParams.has('code')) {
              url.searchParams.delete('code');
              window.history.replaceState({}, '', url.toString());
              if (mounted) nav(targetPath, { replace: true });
              return;
            }
        } else {
          // If we got here via an access_token hash flow, Supabase client may have already detected it.
          await supabase.auth.getSession();
        }

        if (mounted) nav(targetPath, { replace: true });
      } catch (e: unknown) {
        if (mounted) setFatal((e as { message?: string } | null)?.message ?? String(e));
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [nav, targetPath]);

  return (
    <Box mih="100vh" pos="relative">
      <BackgroundImage
        src="/brand/mk_stage_bg.jpg"
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.18,
          filter: 'saturate(0.95) contrast(1.05)',
        }}
      />
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(1100px 620px at 18% 6%, rgba(69, 61, 181, 0.55), transparent 60%), radial-gradient(900px 520px at 88% 4%, rgba(233, 80, 149, 0.22), transparent 55%), linear-gradient(180deg, rgba(43, 38, 34, 0.85), rgba(49, 44, 40, 0.96))',
        }}
      />
      <Center mih="100vh">
        <Paper
          radius="lg"
          p="xl"
          w={520}
          shadow="xl"
          style={{
            backdropFilter: 'blur(10px)',
            background: 'var(--app-surface)',
            border: '1px solid var(--app-border)',
          }}
          className="app-fade-in"
        >
          <Stack gap="md">
            <Box>
              <img
                src={colorScheme === 'dark' ? '/brand/mk_logo_white.png' : '/brand/mk_logo_black.png'}
                alt="Musical!Kultur Daaden e.V."
                style={{ height: 26, width: 'auto', opacity: 0.95 }}
              />
              <Title order={2} c="var(--color-text)">
                Anmeldung…
              </Title>
              <Text c="dimmed">
                Wir verifizieren deinen Link und melden dich an.
              </Text>
            </Box>

            {!ready ? (
              <Text c="dimmed">Bitte warten…</Text>
            ) : fatal ? (
              <>
                <Alert color="red" variant="light" title="Fehler" icon={<IconAlertTriangle size={18} />}>
                  {fatal}
                </Alert>
                <Button variant="light" onClick={() => nav('/login', { replace: true })}>
                  Zum Login
                </Button>
              </>
            ) : (
              <Text c="dimmed">Weiterleitung…</Text>
            )}
          </Stack>
        </Paper>
      </Center>
    </Box>
  );
}
